import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ImpulseSpinner } from 'react-spinners-kit';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import BeaconLogo from 'assets/BeaconLogo.svg';
import tempImage from 'assets/tempImage.jpg';

import {
  getDesignReviewCountsAction,
  getDesignReviewHistoryAction,
  getReviewDataAction,
} from 'Redux/Actions/designReviewActions';
import { setCurrentRecord } from 'Redux/Actions/historyActions';
import { setUserProfile } from 'Redux/Actions/userActions';
import actionTypes from 'Redux/constants';

import { ROUTES } from 'routes/route.constants';

import TeamDashboardReviews, { getDesignReviewJobStatus, getSingleDesignReview } from 'services/teamDashboardReviews';

import { DESIGN_AUDIT_TYPES, DESIGN_REVIEW_STATUS, PAGE_SIZE, PLATFORM } from 'utlis/constants';
import { errorReactToastify } from 'utlis/toasts';
import { isFigmaURL } from 'utlis/validators';

import CategoryDetailView from './components/CategoryDetailView';
import DesignSelection from './components/DesignSelection';
import FlowSelection from './components/FlowSelection';
import ReviewContext from './components/ReviewContext';
import ReviewResults from './components/ReviewResults';
import ShareReviewModal from './components/ShareReviewModal';
import {
  SIMULATE_REVIEW_COMPLETE,
  SIMULATED_CATEGORY,
  SIMULATED_PROBLEM_TAGS,
  SIMULATED_PRODUCT_DESCRIPTION,
  SIMULATED_REVIEW_DATA,
  SIMULATED_SCREENSHOT_UPLOADS,
} from './simulationConfig';

const PAGE_TYPES = {
  FLOW_SELECTION: 'flow selection',
  CONTEXT_ADDITION: 'context addition',
};

const CreateReview = ({ onClose, existingReviewData, isPublicView = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { reviewId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const authUser = useSelector(state => state.userStore);
  const userProfile = useSelector(state => state.userProfile);

  const [selectedCategoryForDetail, setSelectedCategoryForDetail] = useState(null);
  const [currentPage, setCurrentPage] = useState(PAGE_TYPES.FLOW_SELECTION);
  const [selectedCategory, setSelectedCategory] = useState('ux_critique');
  const [completedReviewData, setCompletedReviewData] = useState(null);
  const [selectedProblemChips, setSelectedProblemChips] = useState([]);
  const [showCategoryDetail, setShowCategoryDetail] = useState(false);
  const [showReviewResults, setShowReviewResults] = useState(false);
  const [productDescription, setProductDescription] = useState('');
  const [isReviewComplete, setIsReviewComplete] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [originalFiles, setOriginalFiles] = useState([]);
  const [reviewData, setReviewData] = useState(null);
  const [figmaUrl, setFigmaUrl] = useState('');
  const [figmaFramesData, setFigmaFramesData] = useState(null);
  const [title, setTitle] = useState('');
  const [audience, setAudience] = useState('');
  const [businessGoal, setBusinessGoal] = useState('');
  const [isLoadingReview, setIsLoadingReview] = useState(false);
  const [urlReviewData, setUrlReviewData] = useState(null);

  const jobPollingIntervalRef = useRef(null);
  const latestSubmissionPayloadRef = useRef(null);
  const jobIdRef = useRef(null);
  const processedReviewUuidRef = useRef(null);

  useEffect(() => {
    // Only populate if userStore is empty but localStorage has userObject
    if (!authUser || !authUser.uuid) {
      try {
        const userObjectString = localStorage.getItem('userObject');

        if (userObjectString) {
          const userObject = JSON.parse(userObjectString);

          if (userObject && userObject.uuid) {
            dispatch({
              type: actionTypes.SIGN_IN_USER,
              payload: userObject,
            });
          }
        }
      }
      catch (error) {
        Sentry.captureException(error);
      }
    }
  }, [authUser, dispatch]);

  // Use existingReviewData if provided, otherwise use urlReviewData
  const effectiveReviewData = useMemo(
    () => existingReviewData || urlReviewData,
    [existingReviewData, urlReviewData]
  );

  const getCategoryConfig = categoryKey => {
    switch (categoryKey) {
      case 'onboarding_flow':
        return {
          primingOption: 'onboarding-flow',
          templateLabel: 'User Onboarding',
          problem: '',
          showDeeperInsights: true,
        };
      case 'new_ideas':
        return {
          primingOption: 'new-ideas',
          templateLabel: 'New Ideas',
          problem: '',
          showDeeperInsights: true,
        };
      case 'signup':
        return {
          primingOption: 'ux-critique',
          templateLabel: 'UX Critique',
          problem: 'Analyze the signup flow to spot UX friction & usability issues and streamline the process to make account creation faster, clearer, and more intuitive for new users.',
          showDeeperInsights: true,
        };
      case 'upgrade_pricing':
        return {
          primingOption: 'ux-critique',
          templateLabel: 'UX Critique',
          problem: 'Analyze the upgrade and pricing screens to identify UX friction and clarity gaps, and improve layout and messaging to make plans easier to understand and boost conversion rates.',
          showDeeperInsights: true,
        };
      case 'checkout_payment':
        return {
          primingOption: 'ux-critique',
          templateLabel: 'UX Critique',
          problem: 'Analyze the checkout and payment flow to identify UX friction and usability issues, and streamline the process to make payments smoother, faster, and improve completion success rates',
          showDeeperInsights: true,
        };
      case 'ux_critique':
      default:
        return {
          primingOption: 'ux-critique',
          templateLabel: 'UX Critique',
          problem: '',
          showDeeperInsights: false,
        };
    }
  };

  // Helper function to determine if Share button should be shown
  const showShareButton = useMemo(() => (
    (reviewData && reviewData.review_status === DESIGN_REVIEW_STATUS.DONE && reviewData.uuid)
      || (
        completedReviewData
          && completedReviewData.review_status === DESIGN_REVIEW_STATUS.DONE
          && completedReviewData.uuid
      )
  ), [reviewData, completedReviewData]);

  // Helper function to get the review UUID for sharing
  const getReviewUuid = useMemo(() => (
    reviewData?.uuid || completedReviewData?.uuid || effectiveReviewData?.uuid
  ), [reviewData, completedReviewData, effectiveReviewData]);

  const buildGenerateReviewFormData = ({
    imageFiles = [],
    problemText = '',
    figmaUserId = null,
    category = 'ux_critique',
    figmaUrl: submittedFigmaUrl = '',
    audience: submittedAudience = '',
    businessGoals: submittedBusinessGoals = '',
  }) => {
    const isFileConstructorAvailable = typeof File !== 'undefined';
    const isBlobConstructorAvailable = typeof Blob !== 'undefined';

    const normalizedImageFiles = Array.isArray(imageFiles)
      ? imageFiles.filter(file => (
        (isFileConstructorAvailable && file instanceof File)
        || (isBlobConstructorAvailable && file instanceof Blob)
      ))
      : [];

    const { primingOption, templateLabel, problem, showDeeperInsights } = getCategoryConfig(category);

    const userProblem = [problemText, problem]
      .map(p => (p ?? '').toString().trim())
      .filter(Boolean)
      .join('\n\n');

    const formData = new FormData();

    formData.append('figma_user_id', figmaUserId || '');
    formData.append('page_name', 'Screenshots Page');
    formData.append('file_name', title || normalizedImageFiles[0]?.name);
    formData.append('review_frame_id', '0:0');
    formData.append('review_frame_name', title || normalizedImageFiles[0]?.name);
    formData.append('priming_options', primingOption);
    formData.append('template', templateLabel);
    formData.append('user_problem', userProblem || '');
    formData.append('accessibility_guideline_type', null);
    formData.append('accessibility_guideline_name', null);
    formData.append('brand_guidelines', null);
    formData.append('user_platform', 'web_dashboard');
    formData.append('design_type', DESIGN_AUDIT_TYPES.SCREENSHOT);
    formData.append('audience', submittedAudience || '');
    formData.append('business_goal', submittedBusinessGoals || '');
    formData.append('show_deeper_insights', showDeeperInsights);

    formData.append('review_frame_ids', JSON.stringify(normalizedImageFiles.map(() => '0.0')));
    formData.append('review_frame_names', JSON.stringify(normalizedImageFiles.map(
      // eslint-disable-next-line no-unused-vars
      (_, index) => `Screenshot#${index + 1}`
    )));

    normalizedImageFiles.forEach((file, index) => {
      formData.append(`frame_image_${index}`, file);
      formData.append(`frame_json_${index}`, JSON.stringify({}));
    });

    if (submittedFigmaUrl) {
      formData.append('figma_url', submittedFigmaUrl);
    }

    return formData;
  };

  const downloadFrameImages = useCallback(async frameList => {
    const blobs = await Promise.all(
      frameList.map(async frame => {
        const imageUrl = frame.image_url || frame.imageUrl;

        if (!imageUrl) {
          throw new Error(`Missing image_url for frame: ${frame.frame_id || frame.frame_name}`);
        }

        const response = await fetch(imageUrl);

        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${imageUrl}`);
        }

        return response.blob();
      })
    );

    return blobs;
  }, []);

  // Build FormData for generate_review API when source is Figma frames
  const buildFigmaReviewFormData = useCallback(({
    imageBlobs = [],
    frameList = [],
    sectionName = '',
    fileName = '',
    reviewFrameName = '',
    figmaUserId = '',
    primingOption = 'ux-critique',
    templateLabel = 'UX Critique',
    userProblem = '',
    audienceVal = '',
    businessGoalVal = '',
    showDeeperInsights = false,
  }) => {
    const formData = new FormData();

    imageBlobs.forEach((blob, index) => {
      formData.append(`frame_image_${index}`, blob);
      formData.append(`frame_json_${index}`, JSON.stringify({}));
    });

    formData.append('figma_user_id', figmaUserId || '');
    formData.append('page_name', sectionName || '');
    formData.append('file_name', fileName || '');
    formData.append('review_frame_id', frameList[0]?.frame_id ?? '0:0');
    formData.append('review_frame_ids', JSON.stringify(frameList.map(f => f.frame_id)));
    formData.append('review_frame_name', reviewFrameName || (frameList[0]?.frame_name ?? ''));
    formData.append('review_frame_names', JSON.stringify(frameList.map(f => f.frame_name)));
    formData.append('priming_options', primingOption);
    formData.append('template', templateLabel);
    formData.append('user_problem', userProblem || '');
    formData.append('audience', audienceVal || '');
    formData.append('business_goal', businessGoalVal || '');
    formData.append('user_platform', 'web_dashboard');
    formData.append('design_type', DESIGN_AUDIT_TYPES.FIGMA_FRAME);
    formData.append('show_deeper_insights', showDeeperInsights);
    formData.append('accessibility_guideline_type', null);
    formData.append('accessibility_guideline_name', null);
    formData.append('brand_guidelines', null);

    return formData;
  }, []);

  const stopJobPolling = useCallback(() => {
    if (jobPollingIntervalRef.current) {
      clearInterval(jobPollingIntervalRef.current);
      jobPollingIntervalRef.current = null;
    }
    jobIdRef.current = null;
  }, []);

  const isFormValid = useMemo(
    () => title.trim() !== ''
      && uploadedImages.length > 0,
    [title, uploadedImages]
  );

  const handleJobStatusResponse = useCallback(async statusResponse => {
    const reviewStatus = typeof statusResponse?.review_status === 'number'
      ? statusResponse.review_status
      : null;

    if (Array.isArray(statusResponse?.final_feedback)) {
      setReviewData(statusResponse);
    }

    if (reviewStatus === DESIGN_REVIEW_STATUS.DONE) {
      stopJobPolling();

      dispatch(setCurrentRecord(statusResponse));
      setCompletedReviewData(statusResponse);

      setIsReviewComplete(true);
      setShowReviewResults(true);

      // Navigate to review URL if we have a UUID and we're on the create route
      if (statusResponse?.uuid && !reviewId) {
        navigate(ROUTES.TEAM_REVIEWS_DETAIL(statusResponse.uuid), { replace: true });
      }

      await fetchDesignAuditHistory();
    }

    if (reviewStatus === DESIGN_REVIEW_STATUS.FAILED) {
      stopJobPolling();

      errorReactToastify(
        'The review has failed, please try again later',
        {
          autoClose: 5000,
          position: 'bottom-center',
          hideProgressBar: true,
          style: { backgroundColor: '#FDF0F0', width: '470px' },
        }
      );

      setTimeout(() => {
        if (onClose) {
          onClose();
        }
        else {
          navigate(ROUTES.TEAM_HOME);
        }
      }, 5000);
    }
  }, [fetchDesignAuditHistory, onClose, stopJobPolling, navigate]);

  const fetchJobStatus = useCallback(async activeJobId => {
    if (!activeJobId) {
      return;
    }

    try {
      const statusResponse = await getDesignReviewJobStatus(activeJobId);

      await handleJobStatusResponse(statusResponse);
    }
    catch (error) {
      Sentry.captureException(error);
    }
  }, [handleJobStatusResponse]);

  const startJobPolling = useCallback(activeJobId => {
    if (!activeJobId) {
      return;
    }

    if (jobIdRef.current === activeJobId && jobPollingIntervalRef.current) {
      return;
    }

    stopJobPolling();
    jobIdRef.current = activeJobId;

    fetchJobStatus(activeJobId);

    jobPollingIntervalRef.current = setInterval(() => {
      fetchJobStatus(activeJobId);
    }, 10000);
  }, [fetchJobStatus, stopJobPolling]);

  useEffect(() => {
    if (authUser && authUser.uuid) {
      dispatch(setUserProfile(authUser));
    }
  }, [authUser, dispatch]);

  useEffect(() => {
    if (effectiveReviewData) {
      return;
    }

    const figmaConnected = searchParams.get('figmaConnected');

    if (figmaConnected === 'true') {
      // Refresh user profile to get updated Figma token
      if (authUser && authUser.uuid) {
        dispatch(setUserProfile(authUser, null, true));
      }

      setCurrentPage(PAGE_TYPES.CONTEXT_ADDITION);

      searchParams.delete('figmaConnected');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, effectiveReviewData, authUser, dispatch]);

  // Fetch review data from URL if reviewId is present
  useEffect(() => {
    const fetchReviewFromUrl = async () => {
      // Only fetch if reviewId exists and we don't have existingReviewData or urlReviewData
      if (reviewId && !existingReviewData && !urlReviewData) {
        setIsLoadingReview(true);

        try {
          const fetchedReviewData = await getSingleDesignReview(reviewId);

          if (fetchedReviewData && typeof fetchedReviewData === 'object' && fetchedReviewData.uuid) {
            processedReviewUuidRef.current = null;
            setUrlReviewData(fetchedReviewData);
            dispatch(setCurrentRecord(fetchedReviewData));
          }
          else {
            // Review not found, navigate back
            navigate(ROUTES.TEAM_HOME);
            errorReactToastify(
              'This design review does not exist or you don\'t have permission to view it.',
              {
                autoClose: 5000,
                position: 'bottom-center',
                hideProgressBar: true,
                style: { backgroundColor: '#FDF0F0', width: '530px' },
              }
            );
          }
        }
        catch (error) {
          // Navigate back on error
          navigate(ROUTES.TEAM_HOME);
          errorReactToastify(
            'This design review does not exist or you don\'t have permission to view it.',
            {
              autoClose: 5000,
              position: 'bottom-center',
              hideProgressBar: true,
              style: { backgroundColor: '#FDF0F0', width: '530px' },
            }
          );
        }
        finally {
          setIsLoadingReview(false);
        }
      }
    };

    fetchReviewFromUrl();
  }, [reviewId, existingReviewData, urlReviewData, navigate, dispatch]);

  // Handle existing review data
  useEffect(() => {
    const reviewUuid = effectiveReviewData?.uuid;

    // Reset processed UUID if the review UUID has changed
    if (reviewUuid && processedReviewUuidRef.current !== reviewUuid) {
      processedReviewUuidRef.current = null;
    }

    if (effectiveReviewData && reviewUuid && processedReviewUuidRef.current !== reviewUuid) {
      processedReviewUuidRef.current = reviewUuid;

      // Set review data and populate form
      setReviewData(effectiveReviewData);

      // Set current page to CONTEXT_ADDITION for existing reviews
      setCurrentPage(PAGE_TYPES.CONTEXT_ADDITION);

      // If review is completed, show results
      if (effectiveReviewData.review_status === DESIGN_REVIEW_STATUS.DONE) {
        setIsReviewComplete(true);
        setShowReviewResults(true);
      }
      else if (effectiveReviewData.review_status > DESIGN_REVIEW_STATUS.DONE) {
        const createdAt = new Date(effectiveReviewData.created_at);
        const now = new Date();
        const maxProcessingTime = 20 * 60 * 1000;

        if ((now - createdAt) < maxProcessingTime) {
          startJobPolling(effectiveReviewData.firestore_job_id);
        }
        setShowReviewResults(true);
      }

      // Set uploaded images from design_images
      if (effectiveReviewData.extra_data?.frame_images) {
        const prefixUrl = process.env.REACT_APP_FIREBASE_STORAGE_PREFIX;
        const postfixUrl = '?alt=media';

        const formattedImages = effectiveReviewData.extra_data.frame_images.map((imageUrl, index) => ({
          name: `Review Image ${index + 1}`,
          uid: `existing-${index}`,
          preview: `${prefixUrl}${encodeURIComponent(imageUrl)}${postfixUrl}`,
          url: `${prefixUrl}${encodeURIComponent(imageUrl)}${postfixUrl}`,
          progress: 100,
          isProgressLoading: false,
          isExistingImage: true,
        }));

        setUploadedImages(formattedImages);
      }
      else if (effectiveReviewData.review_status > DESIGN_REVIEW_STATUS.DONE) {
        // eslint-disable-next-line no-unused-vars
        const formattedImages = effectiveReviewData.extra_data.review_frame_names.map((_, index) => ({
          name: `Review Image ${index + 1}`,
          uid: `existing-${index}`,
          preview: tempImage,
          url: tempImage,
          progress: 100,
          isProgressLoading: false,
          isExistingImage: true,
        }));

        setUploadedImages(formattedImages);
      }

      if (effectiveReviewData.audit_data?.category || effectiveReviewData.audit_data?.review_category) {
        setSelectedCategory(effectiveReviewData.audit_data.category || effectiveReviewData.audit_data.review_category);
      }

      const existingProblemTags = effectiveReviewData.audit_data?.problem_tags
        || effectiveReviewData.audit_data?.problemTags;

      if (Array.isArray(existingProblemTags)) {
        setSelectedProblemChips(existingProblemTags);
      }
    }

    // Reset processed UUID if effectiveReviewData becomes null/undefined
    else if (!effectiveReviewData) {
      processedReviewUuidRef.current = null;
    }
  }, [effectiveReviewData, startJobPolling]);

  // Restore TeamDashboard fields from localStorage when component mounts (only for new reviews)
  useEffect(() => {
    if (effectiveReviewData) {
      return;
    } // Skip for existing reviews

    const storedTitle = localStorage.getItem('teamReview_title');
    const storedProductDescription = localStorage.getItem('teamReview_productDescription');
    const storedAudience = localStorage.getItem('teamReview_audience');
    const storedBusinessGoal = localStorage.getItem('teamReview_businessGoal');
    const storedSelectedCategory = localStorage.getItem('teamReview_selectedCategory');
    const storedSelectedProblemChips = localStorage.getItem('teamReview_selectedProblemChips');

    if (storedTitle) {
      setTitle(storedTitle || '');
      localStorage.removeItem('teamReview_title');
    }

    if (storedProductDescription) {
      setProductDescription(storedProductDescription || '');
      localStorage.removeItem('teamReview_productDescription');
    }

    if (storedAudience) {
      setAudience(storedAudience || '');
      localStorage.removeItem('teamReview_audience');
    }

    if (storedBusinessGoal) {
      setBusinessGoal(storedBusinessGoal || '');
      localStorage.removeItem('teamReview_businessGoal');
    }

    if (storedSelectedCategory) {
      setSelectedCategory(storedSelectedCategory);
      localStorage.removeItem('teamReview_selectedCategory');
    }

    if (storedSelectedProblemChips) {
      try {
        const parsedChips = JSON.parse(storedSelectedProblemChips);

        if (Array.isArray(parsedChips)) {
          setSelectedProblemChips(parsedChips);
        }
      }
      catch (error) {
        Sentry.captureException(error);
      }
      localStorage.removeItem('teamReview_selectedProblemChips');
    }
  }, []);

  const shouldSimulateCompletedState = SIMULATE_REVIEW_COMPLETE && !existingReviewData;

  useEffect(() => {
    if (!shouldSimulateCompletedState) {
      return;
    }

    // Skip flow selection for simulation
    setCurrentPage(PAGE_TYPES.CONTEXT_ADDITION);
    setShowReviewResults(true);
    setIsReviewComplete(true);
    setReviewData(SIMULATED_REVIEW_DATA);
    setProductDescription(SIMULATED_PRODUCT_DESCRIPTION || '');
    setTitle(SIMULATED_REVIEW_DATA?.title || '');
    setSelectedCategory(SIMULATED_CATEGORY || 'ux_critique');
    setSelectedProblemChips(Array.isArray(SIMULATED_PROBLEM_TAGS) ? SIMULATED_PROBLEM_TAGS : []);
    setUploadedImages(SIMULATED_SCREENSHOT_UPLOADS);
  }, [
    shouldSimulateCompletedState,
  ]);
  // Cleanup polling on component unmount
  useEffect(() => () => {
    if (jobPollingIntervalRef.current) {
      clearInterval(jobPollingIntervalRef.current);
      jobPollingIntervalRef.current = null;
    }
  }, []);

  // Handle flow selection
  const handleFlowSelect = flowId => {
    setSelectedCategory(flowId);
  };

  const handleFlowNext = () => {
    setCurrentPage(PAGE_TYPES.CONTEXT_ADDITION);
  };

  // Handle share modal
  const handleShareClick = () => {
    setShowShareModal(true);
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
  };

  // Show loading state when on a review detail URL until we have review data
  const isDetailRouteWithoutData = reviewId && !existingReviewData && !urlReviewData;

  if (isDetailRouteWithoutData || isLoadingReview) {
    return (
      <div className="flex justify-center items-center min-h-screen flex-col bg-gray-50">
        <ImpulseSpinner frontColor={'#28BDAE'} />
      </div>
    );
  }

  return (
    <div className="relative w-full h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 flex items-center justify-between px-6 py-3 h-16">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-gray-900">
            {title || effectiveReviewData?.extra_data?.review_frame_name || 'New design review'}
          </span>
        </div>
        <div className="flex items-center gap-4">
          {/* show 'Made with Beacon AI' button on the share page */}
          {isPublicView && showReviewResults && showShareButton && (
            <a
              href={process.env.REACT_APP_ONBEACON_WEBSITE_URL || location.origin}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-row items-center gap-8 bg-white border border-[#E1E4E8]
              rounded-lg px-5 py-2.5 shadow-[0px_2px_4px_-1px_rgba(0,0,0,0.04),0px_1px_1px_rgba(0,0,0,0.02)]"
            >
              <img src={BeaconLogo} alt="onBeacon Logo" className="w-5 h-5" />
              <span className="font-medium text-sm leading-5">
                Made with onBeacon
              </span>
            </a>
          )}
          {/* Share Button - only show when review is completed and NOT in public view */}
          {!isPublicView && showReviewResults && showShareButton && (
            <button
              onClick={handleShareClick}
              className="flex items-center justify-center px-5 py-2.5
              h-10 bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors"
            >
              <span className="text-sm font-medium text-white">
                Share
              </span>
            </button>
          )}
          {!isPublicView && (
            <button
              onClick={() => {
                if (onClose) {
                  onClose();
                }
                else {
                  navigate(ROUTES.TEAM_HOME);
                }
              }}
              className="flex items-center justify-center w-10 h-10 bg-white rounded-full
                hover:bg-gray-50 transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15 5L5 15M5 5l10 10" stroke="#606776" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>
      </header>

      {/* Main Content Area */}
      <div className="flex-1 flex justify-center py-20 overflow-y-scroll">
        {currentPage === PAGE_TYPES.FLOW_SELECTION && !isReviewComplete
          ? (
            <FlowSelection
              selectedFlow={selectedCategory}
              onFlowSelect={handleFlowSelect}
              onNext={handleFlowNext}
            />
          )
          : showCategoryDetail && selectedCategoryForDetail
            ? (
              <CategoryDetailView
                category={selectedCategoryForDetail}
                onBackClick={() => {
                  setShowCategoryDetail(false);
                  setSelectedCategoryForDetail(null);
                }}
                isPublicView={isPublicView}
                onCategoryClick={category => {
                  setSelectedCategoryForDetail(category);
                }}
              />
            )
            : showReviewResults && !isSubmitting
              ? (
                <ReviewResults
                  isReviewComplete={isReviewComplete}
                  reviewData={reviewData}
                  title={title}
                  productDescription={productDescription}
                  audience={audience}
                  businessGoal={businessGoal}
                  onCategoryClick={category => {
                    setSelectedCategoryForDetail(category);
                    setShowCategoryDetail(true);
                  }}
                />
              )
              : (
                <ReviewContext
                  handleSubmitReview={handleSubmitReview}
                  isFormValid={isFormValid}
                  productDescription={productDescription}
                  setProductDescription={setProductDescription}
                  isSubmitting={isSubmitting}
                  title={title}
                  setTitle={setTitle}
                  audience={audience}
                  setAudience={setAudience}
                  businessGoal={businessGoal}
                  goToPrevPage={goToPrevPage}
                  setBusinessGoal={setBusinessGoal}
                  selectedCategory={selectedCategory}
                  setSelectedCategory={setSelectedCategory}
                  selectedProblemChips={selectedProblemChips}
                  setSelectedProblemChips={setSelectedProblemChips}
                />
              )}

        {!showCategoryDetail && (currentPage === PAGE_TYPES.CONTEXT_ADDITION || isReviewComplete) && (
          <DesignSelection
            setUploadedImages={setUploadedImages}
            setOriginalFiles={setOriginalFiles}
            uploadingImages={uploadingImages}
            setFigmaUrl={setFigmaUrl}
            setFigmaFramesData={setFigmaFramesData}
            figmaUrl={figmaUrl}
            uploadedImages={uploadedImages}
            showReviewResults={showReviewResults}
            productDescription={productDescription}
            isSubmitting={isSubmitting}
            title={title}
            audience={audience}
            businessGoal={businessGoal}
            selectedCategory={selectedCategory}
            selectedProblemChips={selectedProblemChips}
          />
        )}
      </div>

      {getReviewUuid && (
        <ShareReviewModal
          isOpen={showShareModal}
          onClose={handleCloseShareModal}
          reviewUuid={getReviewUuid}
        />
      )}
    </div>
  );

  function goToPrevPage() {
    setCurrentPage(PAGE_TYPES.FLOW_SELECTION);
  }

  async function handleSubmitReview() {
    if (SIMULATE_REVIEW_COMPLETE) {
      setShowReviewResults(true);
      setIsReviewComplete(true);
      setReviewData(SIMULATED_REVIEW_DATA);
      setProductDescription(SIMULATED_PRODUCT_DESCRIPTION || productDescription);
      setTitle(SIMULATED_REVIEW_DATA?.title || title);
      setSelectedCategory(SIMULATED_CATEGORY || selectedCategory);
      setSelectedProblemChips(Array.isArray(SIMULATED_PROBLEM_TAGS) ? SIMULATED_PROBLEM_TAGS : []);
      setUploadedImages(SIMULATED_SCREENSHOT_UPLOADS);
      setIsSubmitting(false);

      return;
    }

    if (!isFormValid) {
      return;
    }

    stopJobPolling();

    setIsSubmitting(true);
    setIsReviewComplete(false);
    setReviewData(null);

    try {
      const figmaUserId = authUser?.figma_user_id || userProfile?.figma_user_id || authUser?.figmaUserId || '';
      const isFigmaFlow = figmaUrl && isFigmaURL(figmaUrl) && figmaFramesData?.frames?.length > 0;

      setUploadingImages(true);
      setShowReviewResults(true);

      let formData;

      if (isFigmaFlow) {
        const { frames: figmaFrames, section_name: sectionName } = figmaFramesData;
        const { primingOption, templateLabel, problem, showDeeperInsights } = getCategoryConfig(selectedCategory);
        const userProblem = [productDescription, problem]
          .map(p => (p ?? '').toString().trim())
          .filter(Boolean)
          .join('\n\n');

        const imageBlobs = await downloadFrameImages(figmaFrames);

        formData = buildFigmaReviewFormData({
          imageBlobs,
          frameList: figmaFrames,
          sectionName: sectionName ?? '',
          fileName: sectionName ?? '',
          reviewFrameName: title || '',
          figmaUserId,
          primingOption,
          templateLabel,
          userProblem,
          audienceVal: audience,
          businessGoalVal: businessGoal,
          showDeeperInsights,
        });
      }
      else {
        const normalizedImageFiles = Array.isArray(originalFiles) && originalFiles.length > 0
          ? originalFiles.filter(Boolean)
          : [];

        formData = buildGenerateReviewFormData({
          imageFiles: normalizedImageFiles,
          problemText: productDescription,
          figmaUserId,
          category: selectedCategory,
          figmaUrl: '',
          audience,
          businessGoals: businessGoal,
        });
      }

      latestSubmissionPayloadRef.current = formData;

      const response = await TeamDashboardReviews.submitReview({
        formData,
      });

      // Clear loading state after backend submission completes
      setUploadingImages(false);

      setReviewData({ extra_data: response.data, final_feedback: [], review_status: 4 });

      const responseJobId = response?.data?.job_id || response?.data?.jobId;
      const responseUuid = response?.data?.uuid;

      // If we have a UUID and we're on the create route, navigate to the review URL
      if (responseUuid && !reviewId) {
        navigate(ROUTES.TEAM_REVIEWS_DETAIL(responseUuid), { replace: true });
      }

      if (responseJobId) {
        jobIdRef.current = responseJobId;
        // Start job polling after 30 seconds
        setTimeout(() => {
          startJobPolling(responseJobId);
        }, 30000);
      }

      // Call history and count APIs after successful submission
      await fetchDesignAuditHistory();
    }
    catch (err) {
      setUploadingImages(false);
      errorReactToastify(
        'Failed to submit review. Please try again.',
        {
          autoClose: 5000,
          position: 'bottom-center',
          hideProgressBar: true,
          style: { backgroundColor: '#FDF0F0', width: '470px' },
        }
      );
      setShowReviewResults(false);
      stopJobPolling();
    }
    finally {
      setIsSubmitting(false);
    }
  }

  // Fetch design audit history
  async function fetchDesignAuditHistory(page = 1, days = 30) {
    try {
      await Promise.all([
        dispatch(getDesignReviewHistoryAction(days, page, PAGE_SIZE, PLATFORM)),
        dispatch(getDesignReviewCountsAction(PLATFORM)),
        dispatch(getReviewDataAction()),
      ]);
    }
    catch (error) {
      Sentry.captureException(error);
    }
  }
};

CreateReview.propTypes = {
  onClose: PropTypes.func,
  existingReviewData: PropTypes.object,
  isPublicView: PropTypes.bool,
};

export default CreateReview;
